import { isDefined } from "@omni/utils/helper.utils";

import { isValidQuery } from "@/redux/modules/value/search/helpers/query.helpers";
import {
    SearchConfiguration,
    SearchResults
} from "@/redux/modules/value/search/search.types";
import { AlgoliaSearchHit } from "@/services/algolia/types/search.types";
import { getComponentGroup, isGroup, isType } from "@/utils/search/type.utils";
import { SearchComponentGroup } from "@/utils/search/types/searchComponent";
import { PaginationConfiguration } from "@/utils/search/types/searchConfiguration";
import { ResultListState } from "@/utils/search/types/searchState";

type RequestFilter = (
    configuration: SearchConfiguration,
    state: SearchResults
) => SearchConfiguration;

const SamePageRequestsFilter: RequestFilter = (configuration, state) => {
    const componentGroupsToFilter: Array<SearchComponentGroup> =
        configuration.components
            .map((componentConfiguration) => {
                const paginationConfiguration =
                    componentConfiguration as PaginationConfiguration;
                const { hitsWithInjected, currentPage } =
                    paginationConfiguration;
                if (!isDefined(hitsWithInjected) || !isDefined(currentPage))
                    return;

                const componentConfigurationGroup = getComponentGroup(
                    componentConfiguration.component
                );
                if (!isDefined(componentConfigurationGroup)) return;

                const componentResultListState = state.find(
                    (componentState) =>
                        isType(componentState, "RESULT_LIST") &&
                        isGroup(componentState, componentConfigurationGroup)
                ) as ResultListState<AlgoliaSearchHit> | undefined;

                const stateHits = componentResultListState?.hits.length ?? 0;

                return hitsWithInjected <= stateHits && stateHits !== 0
                    ? componentConfigurationGroup
                    : undefined;
            })
            .filter(isDefined);

    const filteredComponents = configuration.components.filter(
        (componentConfiguration) =>
            !componentGroupsToFilter.includes(
                getComponentGroup(componentConfiguration.component)!
            )
    );

    return {
        ...configuration,
        components: filteredComponents
    };
};

const ExcludeContentPagesOnInvalidQueryRequestFilter: RequestFilter = (
    configuration
) => {
    if (isValidQuery(configuration.query)) return configuration;

    return {
        ...configuration,
        components: configuration.components.filter(
            (componentConfiguration) => {
                return !isGroup(componentConfiguration, "CONTENT");
            }
        )
    };
};

const applyRequestFilters: RequestFilter = (configuration, state) =>
    [
        ExcludeContentPagesOnInvalidQueryRequestFilter,
        SamePageRequestsFilter
    ].reduce(
        (configurationAccumulator, requestFilter) =>
            requestFilter(configurationAccumulator, state),
        configuration
    );

export { applyRequestFilters };
